/**
 * @module app/GivingContext
 * @description Shared giving state for the browse shell: causes, cart, saved
 * causes, and UI overlays (Quick View, cart drawer, toast).
 *
 * One provider and one hook (useGiving). Demo-grade simplification: every
 * consumer re-renders on any change. At 11 causes that is imperceptible; the
 * production build splits this into focused contexts (cart, saved, overlays).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState, type JSX, type ReactNode } from "react";
import { FEATURES } from "../config/tier";
import { causeRepository } from "../data/repositories";
import { cartReducer, cartTotal, type CartAction } from "../domain/cart";
import type { Cause, CartLine, DonorInfo, GiftReceipt } from "../domain/types";
import { readJson, STORAGE_KEYS, writeJson, type StoreKind } from "../lib/storage";

/** Everything the context exposes. */
interface GivingState {
    causes: Cause[] | null;
    causeById: (id: string) => Cause | undefined;
    lines: CartLine[];
    totalUsd: number;
    dispatchCart: (action: CartAction) => void;
    saved: string[];
    toggleSaved: (causeId: string) => void;
    quickViewId: string | null;
    openQuickView: (causeId: string | null) => void;
    cartOpen: boolean;
    setCartOpen: (open: boolean) => void;
    toast: string | null;
    showToast: (message: string) => void;
    donor: DonorInfo;
    setDonor: (donor: DonorInfo) => void;
    receipt: GiftReceipt | null;
    setReceipt: (receipt: GiftReceipt | null) => void;
    paymentFailed: boolean;
    setPaymentFailed: (failed: boolean) => void;
    /** Demo tour: clear causes and load them again so the loading state can be watched. */
    replayLoading: () => void;
}

const GivingContext = createContext<GivingState | null>(null);

/** Empty donor form. */
export const EMPTY_DONOR: DonorInfo = {
    firstName: "", lastName: "", email: "", phone: "", country: "United States", postalCode: "", anonymous: false,
};

/** Cart survives a reload within the visit in every tier (demo safety net). */
const CART_STORE: StoreKind = "session";
/** Saved causes: this visit (Tier 1, brief §4.4) or across visits (Tier 2, Q1314). */
const SAVED_STORE: StoreKind = FEATURES.savedCausesPersist ? "local" : "session";

/** Provides giving state to the app. */
export function GivingProvider({ children }: { children: ReactNode }): JSX.Element {
    const [causes, setCauses] = useState<Cause[] | null>(null);
    const [lines, dispatchCart] = useReducer(cartReducer, undefined, () => readJson<CartLine[]>(CART_STORE, STORAGE_KEYS.cart, []));
    const [saved, setSaved] = useState<string[]>(() => readJson<string[]>(SAVED_STORE, STORAGE_KEYS.saved, []));
    const [quickViewId, openQuickView] = useState<string | null>(null);
    const [cartOpen, setCartOpen] = useState<boolean>(false);
    const [toast, setToast] = useState<string | null>(null);
    const [donor, setDonor] = useState<DonorInfo>(EMPTY_DONOR);
    const [receipt, setReceipt] = useState<GiftReceipt | null>(null);
    const [paymentFailed, setPaymentFailed] = useState<boolean>(false);

    // Load causes through the repository (never the JSON directly). Bumping
    // `loadKey` re-runs the load; that is how the tour's Replay works.
    const [loadKey, setLoadKey] = useState<number>(0);
    useEffect(() => {
        let active: boolean = true;
        void causeRepository.list().then((list: Cause[]) => {
            if (active) setCauses(list);
        });
        return () => {
            active = false;
        };
    }, [loadKey]);
    const replayLoading = useCallback((): void => {
        setCauses(null);
        setLoadKey((k: number) => k + 1);
    }, []);

    useEffect(() => writeJson(CART_STORE, STORAGE_KEYS.cart, lines), [lines]);
    useEffect(() => writeJson(SAVED_STORE, STORAGE_KEYS.saved, saved), [saved]);

    // Toast auto-dismiss; a new toast resets the timer.
    useEffect(() => {
        if (toast === null) return;
        const id: number = window.setTimeout(() => setToast(null), 3200);
        return () => window.clearTimeout(id);
    }, [toast]);

    const causeById = useCallback((id: string): Cause | undefined => causes?.find((c: Cause) => c.id === id), [causes]);
    const toggleSaved = useCallback((causeId: string): void => {
        setSaved((prev: string[]) => (prev.includes(causeId) ? prev.filter((id: string) => id !== causeId) : [...prev, causeId]));
    }, []);

    const value: GivingState = useMemo(() => ({
        causes, causeById, lines, totalUsd: cartTotal(lines), dispatchCart,
        saved, toggleSaved, quickViewId, openQuickView, cartOpen, setCartOpen,
        toast, showToast: setToast, donor, setDonor, receipt, setReceipt, paymentFailed, setPaymentFailed, replayLoading,
    }), [causes, causeById, lines, saved, toggleSaved, quickViewId, cartOpen, toast, donor, receipt, paymentFailed, replayLoading]);

    return <GivingContext.Provider value={value}>{children}</GivingContext.Provider>;
}

/** @returns the giving state. @throws outside GivingProvider (wiring bug, fail loudly). */
export function useGiving(): GivingState {
    const ctx: GivingState | null = useContext(GivingContext);
    if (ctx === null) throw new Error("[GivingContext] useGiving() called outside <GivingProvider>.");
    return ctx;
}
